import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../lib/clerk';
import { api } from '../utils/api';

interface FeatureFlag {
  key: string;
  type: 'BOOLEAN' | 'LIMIT' | 'STRING';
  value: string | number | boolean;
}

export function useFeature(featureKey: string): { enabled: boolean; value: string | number | boolean | undefined; isLoading: boolean } {
  const { orgId } = useAuth();

  const { data: features, isLoading } = useQuery({
    queryKey: ['features', orgId],
    queryFn: async () => {
        const res = await api.get("/tenants/me/features");
        return res.data.data as FeatureFlag[];
    },
    enabled: !!orgId || true
  });

  const feature = useMemo(() => {
      if (!features) return undefined;
      return features.find(f => f.key === featureKey);
  }, [features, featureKey]);

  if (!feature) {
      return { enabled: false, value: undefined, isLoading };
  }

  let enabled = false;
  const val = feature.value;

  if (feature.type === 'BOOLEAN') {
      enabled = val === true || val === 'true';
  } else if (feature.type === 'LIMIT') {
      enabled = (val as number) > 0;
  } else {
      enabled = !!val;
  }

  return { enabled, value: val, isLoading };
}

export function FeatureGuard({ feature, children, fallback = null }: { feature: string, children: React.ReactNode, fallback?: React.ReactNode }) {
    const { enabled, isLoading } = useFeature(feature);

    // If loading, we generally want to avoid layout shifts.
    // However, if we return null, we get a blank space.
    // If we return fallback immediately, we might flash "Access Denied".
    // A spinner might be intrusive.
    // Let's assume for now that if loading, we show nothing (safe default)
    // unless the user wants to handle loading state explicitly in the parent.
    // But since this is a guard, usually we wait.

    if (isLoading) return null;

    if (enabled) return <>{children}</>;
    return <>{fallback}</>;
}
